// ServicesOfProvidersForm.js
import React, { useState, useEffect } from "react";
import { API_IP } from "../../env";
import axios from "axios";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Spinner from "react-bootstrap/Spinner";

// ─── Service icon mapping ──────────────────────────────────────────────────
const SERVICE_ICONS = {
  default: "bi-heart-pulse",
  "personal care": "bi-person-hearts",
  nursing: "bi-bandaid",
  physiotherapy: "bi-activity",
  "occupational therapy": "bi-clipboard2-pulse",
  dementia: "bi-brain",
  palliative: "bi-suit-heart",
  housekeeping: "bi-house-heart",
  companionship: "bi-people",
  transportation: "bi-car-front",
  meal: "bi-egg-fried",
  medication: "bi-capsule",
};

function getServiceIcon(name = "") {
  const lower = name.toLowerCase();
  for (const key in SERVICE_ICONS) {
    if (lower.includes(key)) return SERVICE_ICONS[key];
  }
  return SERVICE_ICONS.default;
}

const GRADIENT_COLORS = [
  ["#667eea", "#764ba2"],
  ["#f093fb", "#f5576c"],
  ["#4facfe", "#00f2fe"],
  ["#43e97b", "#38f9d7"],
  ["#fa709a", "#fee140"],
  ["#a18cd1", "#fbc2eb"],
  ["#ffecd2", "#fcb69f"],
  ["#a1c4fd", "#c2e9fb"],
];

function getGradient(index) {
  const [a, b] = GRADIENT_COLORS[index % GRADIENT_COLORS.length];
  return `linear-gradient(135deg, ${a} 0%, ${b} 100%)`;
}

// ─── Styles ────────────────────────────────────────────────────────────────
const styles = {
  modal: {
    borderRadius: "20px",
    overflow: "hidden",
    border: "none",
  },
  header: {
    background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
    border: "none",
    padding: "1.5rem 2rem",
  },
  body: {
    background: "#f8f9fc",
    padding: "1.5rem",
  },
  footer: {
    background: "#ffffff",
    borderTop: "1px solid #edf2f7",
    padding: "1rem 1.5rem",
  },
  serviceCard: {
    background: "#ffffff",
    borderRadius: "16px",
    border: "1px solid #edf2f7",
    padding: "1.25rem",
    transition: "all 0.25s ease",
    cursor: "default",
    position: "relative",
    overflow: "hidden",
  },
  iconCircle: {
    width: "48px",
    height: "48px",
    borderRadius: "14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.3rem",
    color: "#ffffff",
    flexShrink: 0,
  },
  priceBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    background: "#f0f4ff",
    border: "1px solid #dbeafe",
    borderRadius: "8px",
    padding: "4px 10px",
    color: "#3b82f6",
    fontWeight: 600,
    fontSize: "0.9rem",
    cursor: "pointer",
    transition: "all 0.2s ease",
    userSelect: "none",
  },
  addPanel: {
    background: "#ffffff",
    borderRadius: "16px",
    border: "2px dashed #bfdbfe",
    padding: "1.5rem",
    marginBottom: "1rem",
    transition: "all 0.3s ease",
  },
  emptyState: {
    background: "#ffffff",
    borderRadius: "20px",
    border: "2px dashed #e2e8f0",
    padding: "3rem 2rem",
    textAlign: "center",
  },
  deleteBtn: {
    position: "absolute",
    top: "10px",
    right: "10px",
    background: "transparent",
    border: "none",
    color: "#cbd5e1",
    padding: "4px 6px",
    borderRadius: "8px",
    fontSize: "0.85rem",
    lineHeight: 1,
    transition: "all 0.2s ease",
    cursor: "pointer",
  },
};

// ─── Component ─────────────────────────────────────────────────────────────
function ServicesOfProvidersFrom({ show, onHide, providerID, providerName }) {
  const [serviceLists, setServiceLists] = useState([]);
  const [availableServices, setAvailableServices] = useState([]);
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [newServiceId, setNewServiceId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [hoveredDelete, setHoveredDelete] = useState(null);

  async function req(url, method, data) {
    const token = localStorage.getItem("@uphealth_login_token");
    return await axios({ url, method, headers: { Authorization: `Bearer ${token}` }, data });
  }

  async function fetchServices() {
    if (!providerID) return;
    setIsLoading(true);
    try {
      const { data } = await req(`${API_IP}/api/admin/providers/${providerID}`, "get", []);
      setServiceLists(data.providerServices);
      setAvailableServices(data.availableServices);
    } catch (err) {
      console.error("Failed to fetch services", err);
    }
    setIsLoading(false);
  }

  async function deleteAService(care_service_id) {
    if (!window.confirm("Remove this service from the provider?")) return;
    try {
      await req(`${API_IP}/api/admin/services`, "delete", { provider_id: providerID, care_service_id });
      fetchServices();
    } catch {
      alert("Failed to remove service.");
    }
  }

  async function handleAddService() {
    if (!newServiceId) { alert("Please select a service."); return; }
    setIsSaving(true);
    try {
      await req(`${API_IP}/api/admin/services`, "post", {
        care_service_id: newServiceId,
        provider_id: providerID,
      });
      setNewServiceId("");
      setShowAddPanel(false);
      fetchServices();
    } catch (err) {
      alert("Error: " + (err.response?.data || err.message));
    }
    setIsSaving(false);
  }

  useEffect(() => {
    if (show) {
      fetchServices();
      setShowAddPanel(false);
      setNewServiceId("");
    }
  }, [show, providerID]);

  // ─── Add Service Panel ─────────────────────────────────────────────────
  const AddPanel = () => (
    <div style={styles.addPanel}>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <div className="fw-bold text-dark" style={{ fontSize: "0.95rem" }}>Add a New Service</div>
          <div className="text-muted" style={{ fontSize: "0.8rem" }}>Assign a care service and set the hourly rate</div>
        </div>
        <button
          onClick={() => setShowAddPanel(false)}
          style={{ background: "none", border: "none", color: "#94a3b8", fontSize: "1.1rem", cursor: "pointer", lineHeight: 1 }}
        >
          ×
        </button>
      </div>
      <Row className="g-3 align-items-end">
        <Col md={9}>
          <Form.Label className="text-muted fw-semibold" style={{ fontSize: "0.78rem", letterSpacing: "0.5px", textTransform: "uppercase" }}>
            Service
          </Form.Label>
          <Form.Select
            size="sm"
            value={newServiceId}
            onChange={(e) => setNewServiceId(e.target.value)}
            style={{ borderRadius: "10px", border: "1.5px solid #e2e8f0", fontSize: "0.9rem" }}
          >
            <option value="">-- Select a service --</option>
            {availableServices.map((s) => (
              <option key={s.care_service_id} value={s.care_service_id}>{s.care_service_name}</option>
            ))}
          </Form.Select>
        </Col>
        <Col md={3}>
          <Button
            onClick={handleAddService}
            disabled={isSaving}
            style={{
              width: "100%",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              border: "none",
              borderRadius: "10px",
              fontWeight: 600,
              fontSize: "0.85rem",
              padding: "0.4rem 0",
            }}
          >
            {isSaving ? <Spinner size="sm" animation="border" /> : <><i className="bi bi-check-lg me-1"></i>Save</>}
          </Button>
        </Col>
      </Row>
    </div>
  );

  // ─── Service Card ──────────────────────────────────────────────────────
  const ServiceCard = ({ item, index }) => {
    const isHovered = hoveredCard === item.care_service_id;
    const isDeleteHovered = hoveredDelete === item.care_service_id;

    return (
      <Col xs={12} sm={6} key={item.care_service_id}>
        <div
          style={{
            ...styles.serviceCard,
            boxShadow: isHovered ? "0 8px 30px rgba(0,0,0,0.10)" : "0 1px 4px rgba(0,0,0,0.04)",
            transform: isHovered ? "translateY(-2px)" : "none",
          }}
          onMouseEnter={() => setHoveredCard(item.care_service_id)}
          onMouseLeave={() => setHoveredCard(null)}
        >
          {/* Delete button */}
          <button
            style={{
              ...styles.deleteBtn,
              color: isDeleteHovered ? "#ef4444" : "#cbd5e1",
              background: isDeleteHovered ? "#fef2f2" : "transparent",
            }}
            onMouseEnter={() => setHoveredDelete(item.care_service_id)}
            onMouseLeave={() => setHoveredDelete(null)}
            onClick={() => deleteAService(item.care_service_id)}
            title="Remove service"
          >
            <i className="bi bi-trash"></i>
          </button>

          <div className="d-flex align-items-center gap-3 mb-3">
            {/* Icon */}
            <div style={{ ...styles.iconCircle, background: getGradient(index) }}>
              <i className={`bi ${getServiceIcon(item.care_service_name)}`}></i>
            </div>
            {/* Name */}
            <div style={{ flex: 1, paddingRight: "1.5rem" }}>
              <div className="fw-bold text-dark" style={{ fontSize: "0.95rem", lineHeight: 1.3 }}>
                {item.care_service_name}
              </div>
            </div>
          </div>

          {/* Price section */}
          <div className="d-flex align-items-center gap-2 mt-1">
            <span className="text-muted" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 600 }}>Rate</span>
            <span style={{ ...styles.priceBadge, cursor: "default" }}>
              ${item.hourly_rate ?? "—"}/hr
            </span>
          </div>
        </div>
      </Col>
    );
  };

  // ─── Empty State ───────────────────────────────────────────────────────
  const EmptyState = () => (
    <div style={styles.emptyState}>
      <div style={{
        width: "72px", height: "72px", borderRadius: "20px",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        display: "flex", alignItems: "center", justifyContent: "center",
        margin: "0 auto 1.25rem",
        fontSize: "1.8rem", color: "#fff",
      }}>
        <i className="bi bi-clipboard-plus"></i>
      </div>
      <div className="fw-bold text-dark mb-1" style={{ fontSize: "1.1rem" }}>No services yet</div>
      <div className="text-muted mb-4" style={{ fontSize: "0.88rem" }}>
        Assign your first care service to <strong>{providerName}</strong> to get started.
      </div>
      {!showAddPanel && (
        <Button
          onClick={() => setShowAddPanel(true)}
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            border: "none", borderRadius: "12px",
            padding: "0.55rem 1.75rem", fontWeight: 600, fontSize: "0.9rem",
          }}
        >
          <i className="bi bi-plus-lg me-2"></i>Add First Service
        </Button>
      )}
    </div>
  );

  // ─── Render ────────────────────────────────────────────────────────────
  return (
    <Modal show={show} onHide={onHide} size="lg" centered backdrop="static" contentClassName="border-0 shadow-lg" style={{ borderRadius: "20px" }}>

      {/* Header */}
      <Modal.Header closeButton style={styles.header} className="text-white">
        <div className="d-flex align-items-center gap-3">
          <div style={{
            width: "42px", height: "42px", borderRadius: "12px",
            background: "rgba(255,255,255,0.15)",
            backdropFilter: "blur(10px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "1.1rem", color: "#fff",
          }}>
            <i className="bi bi-card-checklist"></i>
          </div>
          <div>
            <div className="fw-bold" style={{ fontSize: "1rem", lineHeight: 1.2 }}>Care Services</div>
            <div style={{ fontSize: "0.8rem", opacity: 0.7 }}>{providerName}</div>
          </div>
          {serviceLists.length > 0 && (
            <span style={{
              background: "rgba(255,255,255,0.2)",
              backdropFilter: "blur(10px)",
              borderRadius: "20px", padding: "2px 10px",
              fontSize: "0.75rem", fontWeight: 700, color: "#fff",
              marginLeft: "4px",
            }}>
              {serviceLists.length} service{serviceLists.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>
      </Modal.Header>

      {/* Body */}
      <Modal.Body style={styles.body}>
        {showAddPanel && <AddPanel />}

        {isLoading ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <Spinner animation="border" style={{ color: "#667eea" }} />
          </div>
        ) : serviceLists.length === 0 ? (
          <EmptyState />
        ) : (
          <Row className="g-3">
            {serviceLists.map((item, index) => (
              <ServiceCard key={item.care_service_id} item={item} index={index} />
            ))}
          </Row>
        )}
      </Modal.Body>

      {/* Footer */}
      <Modal.Footer style={styles.footer}>
        <Button
          variant="ghost"
          onClick={onHide}
          style={{ background: "none", border: "1.5px solid #e2e8f0", borderRadius: "10px", color: "#64748b", fontWeight: 500, padding: "0.4rem 1.25rem", fontSize: "0.9rem" }}
        >
          Close
        </Button>
        {!showAddPanel && serviceLists.length > 0 && (
          <Button
            onClick={() => setShowAddPanel(true)}
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              border: "none", borderRadius: "10px",
              fontWeight: 600, fontSize: "0.9rem",
              padding: "0.4rem 1.5rem",
              boxShadow: "0 4px 15px rgba(102,126,234,0.4)",
            }}
          >
            <i className="bi bi-plus-lg me-2"></i>Add Service
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default ServicesOfProvidersFrom;
